import type { ChangeEvent, DragEvent, MutableRefObject, ReactNode } from 'react';
import { FileText, Info, Paperclip, Tag as TagIcon, UploadCloud, X } from 'lucide-react';
import { isoToMdy } from '../format';
import type { DocTag, ExistingDocRow, PendingFile } from '../types';
import { DOC_TAGS, DOC_TAG_BY_VALUE, FieldLabel, HelperText, SectionHeading, fileIcon, formatBytes, svInput } from '../ui';

export default function DocsStep(props: {
  remarks: string;
  setRemarks: (v: string) => void;
  pendingFiles: PendingFile[];
  existingRows: ExistingDocRow[];
  removePending: (id: string) => void;
  updatePending: (id: string, patch: Partial<Pick<PendingFile, 'tag' | 'displayName'>>) => void;
  removeExisting: (id: string) => void;
  updateExisting: (id: string, patch: Partial<Pick<ExistingDocRow, 'tag' | 'displayName'>>) => void;
  onPickFiles: (e: ChangeEvent<HTMLInputElement>) => void;
  onDrop: (e: DragEvent<HTMLLabelElement>) => void;
  dragOver: boolean;
  setDragOver: (v: boolean) => void;
  fileInputRef: MutableRefObject<HTMLInputElement | null>;
}) {
  const {
    remarks,
    setRemarks,
    pendingFiles,
    existingRows,
    removePending,
    updatePending,
    removeExisting,
    updateExisting,
    onPickFiles,
    onDrop,
    dragOver,
    setDragOver,
    fileInputRef,
  } = props;

  const totalDocs = pendingFiles.length + existingRows.length;

  return (
    <div className="space-y-7">
      <section>
        <SectionHeading
          icon={Paperclip}
          title="Initial project documents"
          subtitle="Tag each file (DPR, Contract, Drawing…) and rename if needed"
        />
        <div className="mt-3">
          <label
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-4 py-8 transition"
            style={
              dragOver
                ? { borderColor: '#1F5A47', background: 'rgba(51,146,116,0.10)' }
                : { borderColor: 'rgba(11,59,46,0.25)', background: 'rgba(250,247,238,0.6)' }
            }
          >
            <span
              className="flex h-12 w-12 items-center justify-center rounded-2xl"
              style={dragOver ? { background: 'rgba(51,146,116,0.16)', color: '#1F5A47' } : { background: 'rgba(11,59,46,0.08)', color: '#7C8A82' }}
            >
              <UploadCloud className="h-6 w-6" />
            </span>
            <span className="text-sm font-semibold text-[#2A1F17]">
              {dragOver ? 'Drop files to attach' : 'Drag & drop files here, or click to browse'}
            </span>
            <span className="text-xs text-[#7C8A82]">Initial project docs only — DPR, Contracts, Drawings, NOCs, Surveys</span>
            <input ref={fileInputRef} type="file" multiple className="sr-only" onChange={onPickFiles} />
          </label>

          {totalDocs > 0 ? (
            <div className="mt-4 space-y-2.5">
              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#0B3B2E]">
                Attached documents <span className="text-[#7C8A82]">({totalDocs})</span>
              </p>
              {existingRows.map((row) => (
                <DocRow
                  key={row.id}
                  iconElement={<FileText className="h-4 w-4" />}
                  badge={
                    <span className="rounded-md px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider" style={{ background: 'rgba(11,59,46,0.06)', border: '1px solid rgba(11,59,46,0.18)', color: '#7C8A82' }}>
                      Existing
                    </span>
                  }
                  fileName={row.fileName}
                  meta={row.uploadedAt ? `Uploaded ${isoToMdy(row.uploadedAt)}` : undefined}
                  tag={row.tag}
                  displayName={row.displayName}
                  onTagChange={(t) => updateExisting(row.id, { tag: t })}
                  onNameChange={(n) => updateExisting(row.id, { displayName: n })}
                  onRemove={() => removeExisting(row.id)}
                />
              ))}
              {pendingFiles.map((pf) => {
                const Icon = fileIcon(pf.file);
                return (
                  <DocRow
                    key={pf.id}
                    iconElement={<Icon className="h-4 w-4" />}
                    badge={
                      <span className="rounded-md px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider" style={{ background: 'rgba(51,146,116,0.12)', color: '#1F5A47', boxShadow: 'inset 0 0 0 1px rgba(51,146,116,0.35)' }}>
                        New
                      </span>
                    }
                    fileName={pf.file.name}
                    meta={`${formatBytes(pf.file.size)}${pf.file.type ? ` · ${pf.file.type}` : ''}`}
                    tag={pf.tag}
                    displayName={pf.displayName}
                    onTagChange={(t) => updatePending(pf.id, { tag: t })}
                    onNameChange={(n) => updatePending(pf.id, { displayName: n })}
                    onRemove={() => removePending(pf.id)}
                  />
                );
              })}
            </div>
          ) : null}
        </div>
      </section>

      <section>
        <SectionHeading icon={Info} title="Notes" subtitle="Context, risks, handover notes" />
        <div className="mt-3">
          <FieldLabel hint="Optional">Remarks</FieldLabel>
          <textarea
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            rows={4}
            className={`${svInput} resize-y`}
            maxLength={10000}
            placeholder="Project-specific context, risks, or handover notes…"
          />
        </div>
      </section>
    </div>
  );
}

function DocRow({
  iconElement,
  badge,
  fileName,
  meta,
  tag,
  displayName,
  onTagChange,
  onNameChange,
  onRemove,
}: {
  iconElement: ReactNode;
  badge?: ReactNode;
  fileName: string;
  meta?: string;
  tag: DocTag;
  displayName: string;
  onTagChange: (t: DocTag) => void;
  onNameChange: (n: string) => void;
  onRemove: () => void;
}) {
  const tagDef = DOC_TAG_BY_VALUE[tag];
  return (
    <div className="rounded-xl p-3" style={{ border: '1px solid rgba(11,59,46,0.22)', background: 'rgba(252,251,246,0.9)', boxShadow: '0 1px 2px rgba(11,59,46,0.04)' }}>
      <div className="flex items-start gap-3">
        <span
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white"
          style={{ background: tagDef.color }}
        >
          {iconElement}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            {badge}
            <span
              className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider"
              style={{ backgroundColor: tagDef.bg, color: tagDef.color }}
            >
              <TagIcon className="h-2.5 w-2.5" strokeWidth={2.4} />
              {tagDef.short}
            </span>
            <span className="truncate text-[11px] text-[#7C8A82]" title={fileName}>
              {fileName}
            </span>
          </div>
          {meta ? <p className="mt-0.5 text-[11px] text-[#7C8A82]">{meta}</p> : null}
          <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_auto]">
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-[#7C8A82]">
                Display name
              </label>
              <input
                value={displayName}
                onChange={(e) => onNameChange(e.target.value)}
                className={`${svInput} px-2.5 py-1.5`}
                maxLength={200}
                placeholder="e.g. Site Survey Round 1"
              />
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-[#7C8A82]">
                Tag
              </label>
              <select
                value={tag}
                onChange={(e) => onTagChange(e.target.value as DocTag)}
                className={`${svInput} px-2.5 py-1.5 sm:min-w-[160px]`}
              >
                {DOC_TAGS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="shrink-0 rounded-lg p-1.5 text-[#7C8A82] transition hover:bg-[rgba(159,18,57,0.08)] hover:text-[#9F1239]"
          aria-label={`Remove ${fileName}`}
        >
          <X className="h-4 w-4" strokeWidth={2.4} />
        </button>
      </div>
    </div>
  );
}
